/**
 * Configuration → Extractor Service.
 *
 * Two values (memo §3): the URL the callable proxies to, and the shared secret
 * it sends as `X-Extractor-Key`. Both are editable here because the URL IS the
 * migration story — the extractor runs on James's laptop today behind whatever
 * tunnel is up, and moving it to real hardware is "point this field somewhere
 * else", with no code change in either repo.
 *
 * The secret is write-only, on the `saveJiraConfig` pattern: it goes up through
 * a callable and nothing ever reads it back. The status readback returns
 * `secretConfigured: boolean` and no value, so the field shows "configured ✓"
 * and an empty box. Leaving it blank on save keeps the stored secret.
 *
 * The third value is `brandOverrides`, and it is on the readable half of the
 * document on purpose: every surface that decides whether to render a Run button
 * has to resolve a descriptor's brand id the same way this screen does.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { Button } from '../common/Button';
import { Modal } from '../common/Modal';
import { SearchInput } from '../common/SearchInput';
import {
  clearScrapersCache,
  extractorErrorMessage,
  fetchRecentExtractorJobs,
  fetchScrapers,
  getExtractorConfigStatus,
  saveExtractorConfig,
  testExtractorConnection,
  type ExtractorConfigStatus,
  type ExtractorHealth,
  type ScraperDescriptor,
} from '../../services/extractorService';
import { modesForScraper } from '../../services/extractorMatching';
import { formatLastRun, lastRunByExtractorId } from '../../services/extractorJobs';
import {
  brandIdsToVerify,
  buildOverrideRows,
  draftFromOverrides,
  filterScraperRows,
  overridesForSave,
  type BrandOption,
  type ScraperOverrideRow,
} from '../../services/extractorBrandOverrides';
import { fetchBrandListing, isAlgoliaConfigured } from '../../services/algolia';
import { getWatchBrandById, getWatchBrands } from '../../services/firebase';
import { BrandOverrideField } from './BrandOverrideField';

interface ExtractorConfigTabProps {
  /**
   * Opens a resolved brand's references listing, when the host can.
   *
   * The listing rather than the brand editor, because the Run scraper button
   * lives on the listing's toolbar — a click-through from here that landed on
   * the edit modal put the operator on a screen the button had moved off.
   *
   * `runScraper` asks the destination to open its Run scraper modal once it has
   * settled. A REQUEST, not a command: the listing decides, because only it
   * knows whether the feature flag is on and whether a module resolves to the
   * brand. See `ReferenceBrowser` for why this cannot be a click.
   */
  onOpenBrandReferences?: (brandId: string, options?: { runScraper?: boolean }) => void;
}

const NO_OVERRIDES: Record<string, string> = {};

export function ExtractorConfigTab({ onOpenBrandReferences }: ExtractorConfigTabProps = {}) {
  const [status, setStatus] = useState<ExtractorConfigStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [baseUrl, setBaseUrl] = useState('');
  const [secret, setSecret] = useState('');
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [health, setHealth] = useState<ExtractorHealth | null>(null);
  const [scrapers, setScrapers] = useState<ScraperDescriptor[] | null>(null);
  const [loadingScrapers, setLoadingScrapers] = useState(false);

  /* Overrides: what is stored, what the table's boxes currently hold. */
  const [savedOverrides, setSavedOverrides] = useState<Record<string, string>>(NO_OVERRIDES);
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [savingOverrides, setSavingOverrides] = useState(false);
  const [brands, setBrands] = useState<BrandOption[]>([]);
  /** Brands found by doc id that the search index does not carry (disabled ones). */
  const [extraBrands, setExtraBrands] = useState<Record<string, string>>({});
  /** Ids already looked up, so a miss costs one read rather than one per render. */
  const verified = useRef(new Set<string>());
  const [picker, setPicker] = useState<ScraperOverrideRow | null>(null);
  /** Name search over the Registered scrapers table. */
  const [search, setSearch] = useState('');
  /** `extractorId → epoch ms` of that module's most recent admin-started run. */
  const [lastRuns, setLastRuns] = useState<Map<string, number>>(new Map());

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const next = await getExtractorConfigStatus();
      setStatus(next);
      setBaseUrl(next.baseUrl ?? '');
      setSavedOverrides(next.brandOverrides);
    } catch (err) {
      toast.error(extractorErrorMessage(err));
      setStatus(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  /*
   * The brand catalogue behind the override typeahead and the "does this brand
   * exist?" check. Algolia first — the whole index is one request and no
   * Firestore reads — with the collection read as the fallback, on the same
   * reasoning as the brands listing: unconfigured or down must degrade this to
   * slow-and-complete, never to a table that warns about every row.
   */
  useEffect(() => {
    let cancelled = false;
    const toOptions = (rows: { id: string; name: string }[]) =>
      rows.sort((a, b) => a.name.localeCompare(b.name));

    void (async () => {
      try {
        if (isAlgoliaConfigured()) {
          const hits = await fetchBrandListing();
          if (!cancelled) {
            setBrands(
              toOptions(
                hits.map((h) => ({
                  id: h.objectID,
                  name: h.displayName || h.name || h.objectID,
                })),
              ),
            );
            return;
          }
        }
      } catch (err) {
        console.warn('Brand listing unavailable from Algolia, falling back to Firestore', err);
      }
      try {
        const docs = await getWatchBrands();
        if (!cancelled) {
          setBrands(toOptions(docs.map((b) => ({ id: b.id, name: b.name || b.id }))));
        }
      } catch (err) {
        console.warn('Could not load brands for the override picker', err);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  /* A saved map or a fresh catalogue re-seeds the boxes; typing does not. */
  useEffect(() => {
    setDraft(draftFromOverrides(scrapers, savedOverrides));
  }, [scrapers, savedOverrides]);

  const knownBrands = useMemo(() => {
    const map = new Map<string, string>();
    for (const b of brands) map.set(b.id, b.name);
    for (const [id, name] of Object.entries(extraBrands)) map.set(id, name);
    return map;
  }, [brands, extraBrands]);

  /*
   * Disabled brands are evicted from the search index, so an id the catalogue
   * doesn't know is not yet proof the brand is missing — each unknown one gets a
   * single document read before the table is willing to warn about it.
   *
   * Driven by the SAVED map rather than the draft: resolving a half-typed id on
   * every keystroke would be a read per keystroke, and the boxes already resolve
   * against the full catalogue as you type.
   */
  useEffect(() => {
    const unknown = brandIdsToVerify(scrapers, savedOverrides).filter(
      (id) => !knownBrands.has(id) && !verified.current.has(id),
    );
    if (unknown.length === 0) return;

    let cancelled = false;
    for (const id of unknown) verified.current.add(id);
    void (async () => {
      const found: Record<string, string> = {};
      for (const id of unknown) {
        try {
          const brand = await getWatchBrandById(id);
          if (brand) found[id] = brand.name || brand.id;
        } catch (err) {
          console.warn(`Could not verify brand "${id}"`, err);
          verified.current.delete(id);
        }
      }
      if (!cancelled && Object.keys(found).length > 0) {
        setExtraBrands((prev) => ({ ...prev, ...found }));
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [scrapers, savedOverrides, knownBrands]);

  const rows = useMemo(
    () => buildOverrideRows(scrapers, draft, knownBrands),
    [scrapers, draft, knownBrands],
  );

  /** Rows after the name search. Sorted A→Z by `buildOverrideRows` already. */
  const visibleRows = useMemo(() => filterScraperRows(rows, search), [rows, search]);

  /**
   * `extractorId → last run`, derived from the admin's own job collection.
   *
   * Fetched once when the catalogue first arrives, not subscribed: this is a
   * column on a settings table, and a live-updating timestamp here would buy
   * nothing for the reads it costs. `fetchRecentExtractorJobs` swallows a denied
   * read and returns `[]`, so the column degrades to "—" rather than taking the
   * screen down with it.
   */
  useEffect(() => {
    if (scrapers === null) return;
    let cancelled = false;
    void fetchRecentExtractorJobs().then((jobs) => {
      if (!cancelled) setLastRuns(lastRunByExtractorId(jobs));
    });
    return () => {
      cancelled = true;
    };
  }, [scrapers]);

  const overridesDirty = useMemo(() => {
    const next = overridesForSave(draft);
    const keys = new Set([...Object.keys(next), ...Object.keys(savedOverrides)]);
    for (const key of keys) {
      if (next[key] !== savedOverrides[key]) return true;
    }
    return false;
  }, [draft, savedOverrides]);

  const handleSaveOverrides = async () => {
    setSavingOverrides(true);
    try {
      await saveExtractorConfig({ brandOverrides: overridesForSave(draft) });
      toast.success('Brand overrides saved');
      await load();
    } catch (err) {
      toast.error(extractorErrorMessage(err));
    } finally {
      setSavingOverrides(false);
    }
  };

  /**
   * Open a brand's references listing and ask it to start a crawl.
   *
   * Open has always landed on the listing; what is new is the request to run.
   * The listing is where the Run scraper button lives, so an operator who came
   * here, found the module, and clicked Open wanted the crawl — not a second
   * hunt for a button on a page they were just sent to.
   */
  const openBrand = (brandId: string) => {
    setPicker(null);
    onOpenBrandReferences?.(brandId, { runScraper: true });
  };

  const handleSave = async () => {
    const trimmedUrl = baseUrl.trim();
    const trimmedSecret = secret.trim();
    if (trimmedUrl === '' && trimmedSecret === '') {
      toast.error('Nothing to save.');
      return;
    }

    setSaving(true);
    try {
      await saveExtractorConfig({
        ...(trimmedUrl !== '' ? { baseUrl: trimmedUrl } : {}),
        ...(trimmedSecret !== '' ? { secret: trimmedSecret } : {}),
      });
      /*
       * A new URL is a different service, so the session's cached catalogue and
       * its ETag are about a machine we are no longer talking to.
       */
      clearScrapersCache();
      setScrapers(null);
      setHealth(null);
      setSecret('');
      toast.success('Extractor configuration saved');
      await load();
    } catch (err) {
      toast.error(extractorErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setHealth(null);
    try {
      const result = await testExtractorConnection();
      setHealth(result);
      if (result.reachable && result.authOk) {
        toast.success(`Connected — ${result.scraperCount} scraper(s) registered`);
      } else {
        toast.error(result.detail ?? 'Could not reach the extractor');
      }
    } catch (err) {
      toast.error(extractorErrorMessage(err));
    } finally {
      setTesting(false);
    }
  };

  const handleLoadScrapers = async () => {
    setLoadingScrapers(true);
    try {
      setScrapers(await fetchScrapers(true));
    } catch (err) {
      toast.error(extractorErrorMessage(err));
    } finally {
      setLoadingScrapers(false);
    }
  };

  if (loading) {
    return <div className="text-sm text-gray-500">Loading extractor configuration…</div>;
  }

  return (
    <div className="max-w-3xl space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-gray-900">Extractor Service</h2>
        <p className="mt-1 text-sm text-gray-500">
          Where the admin sends scraper runs. The extractor is a service running on its own
          machine; these two values are all that point at it, so moving it to different hardware
          is a change here and nothing else.
        </p>
      </div>

      <div className="rounded-lg border border-gray-200 bg-white p-4 space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="extractor-url">
            Base URL
          </label>
          <input
            id="extractor-url"
            type="url"
            value={baseUrl}
            onChange={(e) => setBaseUrl(e.target.value)}
            placeholder="https://extractor.watchkeeper.tech"
            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
          />
          <p className="mt-1 text-xs text-gray-500">
            An origin with no path — every extractor route is mounted at the root. Calls are made
            from Cloud Functions, so this must be reachable from Google&apos;s network:
            <code className="px-1 bg-gray-100 rounded mx-1">localhost</code> will not work, but an
            ngrok URL during local development will.
          </p>
        </div>

        <div>
          <label
            className="block text-sm font-medium text-gray-700 mb-1"
            htmlFor="extractor-secret"
          >
            Shared secret
            {status?.secretConfigured && (
              <span className="ml-2 text-xs font-normal text-green-700">configured ✓</span>
            )}
          </label>
          <input
            id="extractor-secret"
            type="password"
            autoComplete="new-password"
            value={secret}
            onChange={(e) => setSecret(e.target.value)}
            placeholder={
              status?.secretConfigured ? 'Leave blank to keep the stored secret' : 'At least 16 characters'
            }
            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
          />
          <p className="mt-1 text-xs text-gray-500">
            Sent as <code className="px-1 bg-gray-100 rounded">X-Extractor-Key</code> and must match
            the service&apos;s <code className="px-1 bg-gray-100 rounded">EXTRACTOR_API_KEY</code>.
            Write-only: it is stored where only the Cloud Functions can read it, never returned to
            this page, and never in the browser bundle.
            {status?.secretFromEnvironment && (
              <>
                {' '}
                Currently coming from the function environment rather than the database — saving
                here overrides it.
              </>
            )}
          </p>
        </div>

        <div className="flex items-center gap-2 pt-1">
          <Button onClick={handleSave} loading={saving} disabled={saving}>
            Save
          </Button>
          <Button
            variant="secondary"
            onClick={handleTest}
            loading={testing}
            disabled={testing || !status?.configured}
          >
            Test connection
          </Button>
          {!status?.configured && (
            <span className="text-xs text-gray-500">
              Save a URL and a secret before testing.
            </span>
          )}
        </div>

        {health && (
          <div
            className={`rounded-lg border p-3 text-sm ${
              health.reachable && health.authOk
                ? 'border-green-200 bg-green-50 text-green-800'
                : 'border-red-200 bg-red-50 text-red-800'
            }`}
          >
            <div className="font-medium">
              {health.reachable
                ? health.authOk
                  ? `Reachable and authenticated — ${health.scraperCount} scraper(s)`
                  : 'Reachable, but the secret was rejected'
                : 'Not reachable'}
            </div>
            {health.detail && <div className="mt-1 text-xs">{health.detail}</div>}
            <div className="mt-1 text-xs opacity-75">{health.baseUrl}</div>
          </div>
        )}

        {status?.updatedAt && (
          <p className="text-xs text-gray-400">
            Last changed {new Date(status.updatedAt).toLocaleString()}
            {status.updatedBy ? ` by ${status.updatedBy}` : ''}
          </p>
        )}
      </div>

      <div className="rounded-lg border border-gray-200 bg-white p-4 space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-sm font-medium text-gray-700">Registered scrapers</div>
            <p className="text-xs text-gray-500">
              What the service reports it can crawl. A brand only gets a Run scraper button when a
              module here resolves to its brand id — set an override when the module&apos;s slug and
              the brand document&apos;s id disagree.
            </p>
          </div>
          <Button
            variant="secondary"
            size="sm"
            onClick={handleLoadScrapers}
            loading={loadingScrapers}
            disabled={loadingScrapers || !status?.configured}
          >
            Refresh
          </Button>
        </div>

        {scrapers === null ? (
          <p className="text-xs text-gray-400">Not loaded.</p>
        ) : scrapers.length === 0 ? (
          <p className="text-xs text-gray-400">The service reported no scrapers.</p>
        ) : (
          <>
            <SearchInput
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onClear={() => setSearch('')}
              placeholder="Search modules…"
              aria-label="Search modules by name"
            />

            {visibleRows.length === 0 ? (
              <p className="text-xs text-gray-400">Nothing matches your search.</p>
            ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wide text-gray-500">
                    <th className="py-2 pr-4">Module</th>
                    <th className="py-2 pr-4">Brands</th>
                    <th className="py-2 pr-4">Override</th>
                    <th className="py-2 pr-4">Modes</th>
                    <th className="py-2 pr-4">Version</th>
                    <th
                      className="py-2 pr-4"
                      title={
                        'The most recent run STARTED FROM THIS ADMIN. The extractor publishes no ' +
                        'run history, so this is derived from the admin\u2019s own job records: a crawl ' +
                        'started from the extractor\u2019s CLI leaves no trace here, only the most recent ' +
                        'jobs are read, and the read is permission-gated \u2014 in all three cases the ' +
                        'cell shows \u201c\u2014\u201d rather than a wrong date.'
                      }
                    >
                      Last run from admin
                    </th>
                    <th className="py-2" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {visibleRows.map((row) => {
                    const { descriptor, isAllBrands, brands: resolved, hasUnresolved, openable } =
                      row;
                    return (
                    <tr key={descriptor.id} className="align-top">
                      <td className="py-2 pr-4">
                        <div className="font-medium text-gray-900">{descriptor.displayName}</div>
                        <div className="text-xs text-gray-500">{descriptor.id}</div>
                      </td>
                      <td className="py-2 pr-4 text-xs text-gray-600">
                        <span>
                          {isAllBrands
                            ? 'all'
                            : resolved.map((b) => b.descriptorBrandId).join(', ') || '—'}
                        </span>
                        {hasUnresolved && (
                          <span
                            className="ml-1 text-amber-600"
                            role="img"
                            aria-label="No brand found"
                            title="No brand found — set an override or add the brand"
                          >
                            ⚠
                          </span>
                        )}
                        {resolved.some((b) => b.overrideId) && (
                          <div className="mt-0.5 text-gray-400">
                            →{' '}
                            {resolved
                              .map((b) => (b.overrideId ? `${b.brandId}` : b.descriptorBrandId))
                              .join(', ')}
                          </div>
                        )}
                      </td>
                      <td className="py-2 pr-4">
                        {isAllBrands ? (
                          <span className="text-xs text-gray-400">n/a</span>
                        ) : (
                          <div className="space-y-1">
                            {resolved.map((b) => (
                              <BrandOverrideField
                                key={b.descriptorBrandId}
                                descriptorBrandId={b.descriptorBrandId}
                                value={draft[b.descriptorBrandId] ?? ''}
                                onChange={(next) =>
                                  setDraft((prev) => ({ ...prev, [b.descriptorBrandId]: next }))
                                }
                                brands={brands}
                                disabled={savingOverrides}
                              />
                            ))}
                          </div>
                        )}
                      </td>
                      <td className="py-2 pr-4 text-xs text-gray-600">
                        {modesForScraper(descriptor).join(', ')}
                      </td>
                      <td className="py-2 pr-4 text-xs text-gray-600">{descriptor.version}</td>
                      <td className="py-2 pr-4 text-xs text-gray-600 whitespace-nowrap">
                        {lastRuns.has(descriptor.id) ? (
                          <span title={new Date(lastRuns.get(descriptor.id)!).toLocaleString()}>
                            {formatLastRun(lastRuns.get(descriptor.id))}
                          </span>
                        ) : (
                          <span className="text-gray-300" title="No run started from this admin in the recent job history.">
                            —
                          </span>
                        )}
                      </td>
                      <td className="py-2 text-right">
                        {onOpenBrandReferences && openable.length > 0 && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() =>
                              openable.length === 1 ? openBrand(openable[0].id) : setPicker(row)
                            }
                          >
                            Open →
                          </Button>
                        )}
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            )}

            <div className="flex items-center gap-2 pt-1">
              <Button
                size="sm"
                onClick={handleSaveOverrides}
                loading={savingOverrides}
                disabled={savingOverrides || !overridesDirty}
              >
                Save overrides
              </Button>
              <span className="text-xs text-gray-500">
                {overridesDirty
                  ? 'Unsaved override changes.'
                  : 'An override points a module’s brand slug at the brand document it should have matched.'}
              </span>
            </div>
          </>
        )}
      </div>

      <Modal
        isOpen={picker !== null}
        onClose={() => setPicker(null)}
        title={picker ? `Open a brand — ${picker.descriptor.displayName}` : 'Open a brand'}
        size="sm"
      >
        <div className="space-y-2">
          <p className="text-sm text-gray-500">
            This module covers more than one brand. Pick whose references to open.
          </p>
          {picker?.openable.map((b) => (
            <button
              key={b.id}
              type="button"
              onClick={() => openBrand(b.id)}
              className="w-full text-left border border-gray-200 rounded-lg px-3 py-2 hover:bg-gray-50"
            >
              <div className="text-sm text-gray-900">{b.name}</div>
              <div className="text-xs text-gray-500">{b.id}</div>
            </button>
          ))}
        </div>
      </Modal>
    </div>
  );
}
